import { defineComponent as _defineComponent } from 'vue'
import { aiChatConversationDelete, aiChatConversationList, aiChatConversationMessageGet, aiChatStream } from '@/api/moudules/aiChat';
import { IAiChatStreamHistory, IAiConversionItem } from '@/api/moudules/aiChat.types';
import { ref, computed, watch, nextTick, onMounted } from 'vue'

import aiAvatar from '@/assets/aiChat/ai_robot.png'
import userAvatar from '@/assets/aiChat/user.png'
import { nanoid } from 'nanoid';
import { useReport } from '@/hooks/useReport';
import _ from 'lodash';
import MarkdownRender from '../MarkdownRender.vue';
import { formatDateTime } from '@/utils/datetime';
import { useI18n } from '@/hooks/useI18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'AiChatDialog',
  props: {
    visible: { type: Boolean },
    title: null
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'close')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    visible?: boolean
    title?: string
}





const R = useReport()
const {
    $t,
} = useI18n()

const searchKeyword = ref('')
const inputMessage = ref('')
const currentChatIndex = ref(-1)
const currentConversationId = ref<string>('')
const messagesContainer = ref<HTMLElement | null>(null)
const loading = ref(false)

// 历史对话列表
const historyList = ref<IAiConversionItem[]>([])

// 聊天消息列表
const messages = ref<IAiChatStreamHistory[]>([])


const enableInput = ref(true)

function resetInputState() {
    enableInput.value = true
}

const enableInputReal = computed(() => {
    return enableInput.value
})

const filteredHistoryList = computed(() => {
    if (!searchKeyword.value) {
        return historyList.value
    }
    return historyList.value.filter(item =>
        item.title.toLowerCase().includes(searchKeyword.value.toLowerCase())
    )
})

function onInputSearchKeyword() {
    currentChatIndex.value = -1
}

const handleClose = () => {
    emit('close')
}

const handleNewChat = async () => {
    let conversationId = nanoid()
    try {
        loading.value = true

        messages.value = []
        currentConversationId.value = conversationId
        currentChatIndex.value = 0
        // 刷新对话列表
        await loadConversationList()
        historyList.value = [{
            id: conversationId,
            createTime: formatDateTime(),
            title: '',
            updateTime: formatDateTime(),
        }, ...historyList.value]
        resetInputState()
    } catch (error) {
        R.showErrorMessage(`${$t('aiChat.createConversationFailed')}: ${error}`)
    } finally {
        loading.value = false
    }
}

const selectChat = async (index: number) => {
    currentChatIndex.value = index
    const conversation = filteredHistoryList.value[index]
    if (conversation) {
        currentConversationId.value = conversation.id
        await loadMessageHistory(conversation.id)
        resetInputState()
    }
}

// 加载消息历史
const loadMessageHistory = async (conversationId: string) => {
    try {
        loading.value = true
        const res = await aiChatConversationMessageGet(conversationId)
        messages.value = _.map(res as any || [], x => {
            return x
        })
    } catch (error) {
        console.error(`${$t('aiChat.loadMessageHistoryFailed')}:`, error)
    } finally {
        loading.value = false
    }
}

// 加载对话列表
const loadConversationList = async () => {
    try {
        const res = await aiChatConversationList()
        historyList.value = _.map(res as any || [], x => {
            return x
        })
    } catch (error) {
        console.error(`${$t('aiChat.loadConversationListFailed')}:`, error)
    }
}

const enableScroll = ref(false)
const handleSend = async () => {
    enableScroll.value = true
    if (!inputMessage.value.trim()) return
    let message = inputMessage.value.trim()
    inputMessage.value = ''
    let conversationId = currentConversationId.value
    messages.value.push({
        content: message,
        role: 'user',
        conversationId,
        createTime: formatDateTime()
    })
    const history = _.cloneDeep(messages.value)
    const assistantMessage: IAiChatStreamHistory = {
        content: '',
        role: 'assistant',
        conversationId,
        createTime: formatDateTime(),
        loading: true
    }
    messages.value.push(assistantMessage)
    enableInput.value = false
    // 调用AI聊天API
    aiChatStream({
        conversationId,
        message,
        history,
    }, {
        onopen(response) {
            console.log(response)
        },
        onmessage(e) {
            let {
                event,
                data
            } = e
            if (event === 'message') {
                let message = { v: '' }
                try {
                    message = JSON.parse(data)
                } catch (e) {
                    console.error(e)
                }
                assistantMessage.loading = false
                assistantMessage.content += message.v
                scrollToBottom();
            }
            // console.log(e)
        },
        onerror(error) {
            assistantMessage.loading = false
            R.showErrorMessage(`${$t('aiChat.sendMessageFailed')}: ${error}`)
            resetInputState()
        },
        onclose() {
            assistantMessage.loading = false
            resetInputState()
        }
    })
}

function onMouseWheel(e: MouseEvent) {
    let dom = messagesContainer.value as HTMLDivElement
    const {
        scrollHeight,
        clientHeight,
        scrollTop
    } = dom
    if (scrollHeight - scrollTop - clientHeight < 30) {
        enableScroll.value = true
    } else {
        enableScroll.value = false
    }
}

const scrollToBottom = () => {
    if (messagesContainer.value && enableScroll.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
}

// 组件挂载时加载对话列表
onMounted(() => {
    loadConversationList().then(() => {
        handleNewChat()
    })
})

watch(() => props.visible, (val) => {
    if (val) {
        nextTick(() => {
            scrollToBottom()
        })
    }
})

function onClickRemoveHistory(item: IAiConversionItem) {
    let lastConversationId = currentConversationId.value
    aiChatConversationDelete(item.id).then(() => {
        loadConversationList().then(() => {
            let cid = filteredHistoryList.value.findIndex(x => x.id === lastConversationId)
            if (cid >= 0) {
                selectChat(cid)
            } else {
                handleNewChat()
            }
        })
    })
}

watch(messages, () => {
    nextTick(() => {
        scrollToBottom()
    })
}, { deep: true })

const activing = ref(false)

function onFocus() {
    activing.value = true
}
function onBlur() {
    activing.value = false
}


const showClear = computed(() => {
    return Boolean(searchKeyword.value)
})
function onClear() {
    searchKeyword.value = ''
}


return { __sfc: true,props, emit, R, $t, searchKeyword, inputMessage, currentChatIndex, currentConversationId, messagesContainer, loading, historyList, messages, enableInput, resetInputState, enableInputReal, filteredHistoryList, onInputSearchKeyword, handleClose, handleNewChat, selectChat, loadMessageHistory, loadConversationList, enableScroll, handleSend, onMouseWheel, scrollToBottom, onClickRemoveHistory, activing, onFocus, onBlur, showClear, onClear, aiAvatar, userAvatar, MarkdownRender }
}

})